import { Link } from '@/components/atoms/Link';
import TextButtonLink from '@/components/atoms/TextButtonLink/TextButtonLink';
import { CheckAvailabilityButtonProps } from '@/components/molecules/CheckAvailabilityButton';
import { BaseEstimatedCalculatorProps } from '@/components/molecules/EstimatedCalculator';
import { DealerType } from '@/lib/schema/dealer/types';
import {
  JDPowerIncentive,
  VehicleIncentivesData,
} from '@/lib/schema/incentives/types';
import { ChevronRightIcon, MapPinIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import React from 'react';
import { EVSavingsSummaryAccordion } from '../EVSavingsSummaryAccordion/EVSavingsSummaryAccordion';
import { VDPCheckAvailabiltyCTABar } from '../VDPCheckAvailabiltyCTABar/VDPCheckAvailabiltyCTABar';
import { VehiclePrice, VehiclePriceProps } from '../VehiclePrice/VehiclePrice';

export interface BaseVDPUserControlPanelProps {
  showBenefits: boolean;
}

export interface VDPUserControlPanelProps
  extends Omit<BaseEstimatedCalculatorProps, 'monthlyEstimatedPayment'>,
    BaseVDPUserControlPanelProps,
    Pick<CheckAvailabilityButtonProps, 'contactedDealer'> {
  handleSendMessage: () => void;
  handleScrollToCalculator: () => void;
  location?: string;
  dealer?: DealerType | null;
  productId: string;
  showNewCheckAvailabilityButton: boolean;
  setOpenCallModal: () => void;
  showCallModalButton?: boolean;
  isSpecsModal?: boolean;
  productPrice: number | null;
  messageDealer: () => Promise<void>;
  fuelSavingsEstimate?: string;
  annualMiles?: string;
  gasPerGallon?: string;
  electricityPerKwh?: string;
  incentivesData?: VehicleIncentivesData;
  selectedIncentives?: JDPowerIncentive[];
  isActive: boolean;
  backGroundImage: string;
  isAuthDialogOpen: boolean;
  setIsAuthDialogOpen: (isOpen: boolean) => void;
  openIncentiveModal?: () => void;
  handleEmailNotification: () => Promise<void>;
  vehiclePriceProps: Omit<VehiclePriceProps, 'variant'>;
}

export const VDPUserControlPanel: React.FC<VDPUserControlPanelProps> = ({
  messageDealer,
  handleScrollToCalculator,
  dealer,
  productId,
  showCallModalButton = true,
  setOpenCallModal = () => {},
  isSpecsModal = false,
  contactedDealer,
  fuelSavingsEstimate,
  annualMiles,
  gasPerGallon,
  electricityPerKwh,
  incentivesData,
  selectedIncentives,
  isActive,
  backGroundImage = '',
  isAuthDialogOpen = false,
  setIsAuthDialogOpen = () => {},
  openIncentiveModal = () => {},
  vehiclePriceProps: {
    price,
    monthlyEstimatedPayment,
    appliedIncentiveTotal,
    totalEstFuelSavingsPerYear,
    gasTerm,
  },
  handleEmailNotification,
}) => {
  const fullAddress = dealer
    ? `${dealer.address}, ${dealer.city}, ${dealer.province}, ${dealer.postalCode}`
    : '';
  return (
    <div
      className={clsx(
        'relative flex flex-col gap-l rounded-small border-neutral-200 bg-white ml:sticky ml:top-[110px]',
        {
          'ml:border': !isSpecsModal,
          border: isSpecsModal,
        }
      )}
    >
      <div className="flex flex-col py-l">
        {isActive && (
          <>
            {isSpecsModal && dealer && (
              <div className="flex flex-col px-xl">
                <TextButtonLink
                  aria-label="View dealer inventory"
                  icon={{
                    icon: <ChevronRightIcon className="size-[20px]" />,
                    position: 'right',
                  }}
                  href={`/dealer/${dealer.dealerID}`}
                  target="_blank"
                >
                  All EVs
                </TextButtonLink>
                {dealer.address ? (
                  <div className="flex gap-xs">
                    <div>
                      <MapPinIcon className="size-[20px] fill-blue-medium" />
                    </div>
                    <Link
                      target="_blank"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                    >
                      <div className="text-body2Medium text-blue-medium">
                        {fullAddress}
                      </div>
                    </Link>
                  </div>
                ) : null}
              </div>
            )}

            <div
              className={clsx('', {
                'hidden ml:flex': !isSpecsModal,
                flex: isSpecsModal,
              })}
            >
              <VDPCheckAvailabiltyCTABar
                showCallModalButton={showCallModalButton}
                dealer={dealer}
                productId={productId}
                setOpenCallModal={setOpenCallModal}
                contactedDealer={contactedDealer}
                backGroundImage={backGroundImage}
                isAuthDialogOpen={isAuthDialogOpen}
                setIsAuthDialogOpen={setIsAuthDialogOpen}
                messageDealer={messageDealer}
                handleEmailNotification={handleEmailNotification}
              />
            </div>
          </>
        )}
        <div className="px-xl">
          <VehiclePrice
            price={price || 0}
            monthlyEstimatedPayment={monthlyEstimatedPayment}
            handleScrollToCalculator={handleScrollToCalculator}
            appliedIncentiveTotal={appliedIncentiveTotal}
            totalEstFuelSavingsPerYear={totalEstFuelSavingsPerYear}
            gasTerm={gasTerm}
            variant={'VDPUserControlPanel'}
          />
        </div>
      </div>
      <div className="flex h-px w-full border-b border-neutral-300" />
      <div className="px-xl pb-xl">
        <EVSavingsSummaryAccordion
          incentivesData={incentivesData}
          selectedIncentives={selectedIncentives}
          incentivesTotal={appliedIncentiveTotal}
          openIncentiveModal={openIncentiveModal}
          gasSavings={
            fuelSavingsEstimate
              ? {
                  timePeriod: String(gasTerm),
                  fuelSavingsEstimate,
                  annualMiles,
                  gasPerGallon,
                  electricityPerKwh,
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};
